<template>
    <section class="dp-layout psychology-form-config-page">
        <!-- 左侧学校树 -->
        <section class="dp-left">
            <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1"></DepartmentPane>
        </section>

        <!-- 右侧表单与选项配置 -->
        <section class="dp-right">
            <template v-if="hasSchoolSelected">
                <!-- 顶部选择校区栏 -->
                <div class="header-bar">
                    <div class="school-info">
                        <span class="label">当前学校：</span>
                        <span class="value">{{ schoolName || '未命名学校' }}</span>
                    </div>
                    <div class="campus-selector">
                        <span class="label">选择校区：</span>
                        <el-select v-model="campusId" placeholder="请选择校区" size="small" class="campus-select" @change="handleCampusChange">
                            <el-option v-for="item in campusOptions" :key="item.Id" :label="item.Name" :value="item.Id" />
                        </el-select>
                        <el-button
                            class="copy-campus-btn"
                            size="small"
                            type="primary"
                            plain
                            icon="el-icon-document-copy"
                            :disabled="!campusId || campusOptions.length <= 1"
                            @click="openCopyDialog"
                        >
                            复制其他校区配置
                        </el-button>
                    </div>
                </div>

                <div v-if="!campusId" class="no-campus-tip">
                    <i class="el-icon-info"></i>
                    <span>请先在上方下拉框中选择要配置的校区</span>
                </div>

                <el-tabs v-else v-model="activeTab" class="form-config-tabs" @tab-click="handleTabClick">
                    <!-- Tab 1: 心理预约表单字段 -->
                    <el-tab-pane label="心理预约表单字段" name="appointment">
                        <div class="tab-toolbar">
                            <div class="toolbar-tip">
                                <i class="el-icon-info"></i> 配置班主任在发起心理预约时填写的字段目录，支持控制显示、必填与自定义名称
                            </div>
                            <div class="toolbar-actions">
                                <el-button size="small" type="primary" icon="el-icon-check" :loading="savingFields" @click="saveFields('appointment')">
                                    批量保存字段
                                </el-button>
                                <el-button size="small" type="warning" plain icon="el-icon-refresh-left" @click="resetFields('appointment')">
                                    恢复系统默认
                                </el-button>
                            </div>
                        </div>

                        <div class="tbl-scroll-host">
                            <el-table :data="appointmentFields" v-loading="loadingFields" border class="compact-table" height="100%" style="width: 100%;">
                                <el-table-column prop="FieldKey" label="字段标识" width="150" />
                                <el-table-column label="显示名称（可修改）" width="240">
                                    <template slot-scope="scope">
                                        <el-input v-model="scope.row.Label" size="small" placeholder="请输入显示名称" />
                                    </template>
                                </el-table-column>
                                <el-table-column prop="SectionTitle" label="所属分组" min-width="140" show-overflow-tooltip />
                                <el-table-column prop="FieldType" label="控件类型" width="120">
                                    <template slot-scope="scope">
                                        <el-tag size="mini">{{ formatFieldType(scope.row.FieldType) }}</el-tag>
                                    </template>
                                </el-table-column>
                                <el-table-column label="占位提示文案" min-width="180">
                                    <template slot-scope="scope">
                                        <el-input v-model="scope.row.Placeholder" size="small" placeholder="提示说明文案" />
                                    </template>
                                </el-table-column>
                                <el-table-column label="是否显示" width="100" align="center">
                                    <template slot-scope="scope">
                                        <el-switch v-model="scope.row.IsVisible" :disabled="scope.row.FieldKey === 'studentId' || scope.row.FieldKey === 'appointDate'" />
                                    </template>
                                </el-table-column>
                                <el-table-column label="是否必填" width="100" align="center">
                                    <template slot-scope="scope">
                                        <el-switch v-model="scope.row.IsRequired" :disabled="scope.row.FieldKey === 'studentId' || scope.row.FieldKey === 'appointDate'" />
                                    </template>
                                </el-table-column>
                                <el-table-column label="排序" width="105" align="center" class-name="sort-order-col">
                                    <template slot-scope="scope">
                                        <el-input-number v-model="scope.row.SortOrder" size="small" :min="0" :max="999" controls-position="right" class="sort-number-input" />
                                    </template>
                                </el-table-column>
                            </el-table>
                        </div>
                    </el-tab-pane>

                    <!-- Tab 2: 家长（学生心理档案） -->
                    <el-tab-pane label="家长（学生心理档案）" name="archiveParent">
                        <div class="tab-toolbar">
                            <div class="toolbar-tip">
                                <i class="el-icon-info"></i> 配置家长填写「学生心理档案」时的字段，支持显示、必填、改名与选项
                            </div>
                            <div class="toolbar-actions">
                                <el-button size="small" type="primary" icon="el-icon-check" :loading="savingFields" @click="saveFields('archiveParent')">批量保存字段</el-button>
                                <el-button size="small" type="warning" plain icon="el-icon-refresh-left" @click="resetFields('archiveParent')">恢复系统默认</el-button>
                            </div>
                        </div>
                        <div class="tbl-scroll-host">
                            <el-table :data="parentFields" v-loading="loadingFields" border class="compact-table" height="100%" style="width: 100%;">
                                <el-table-column prop="FieldKey" label="字段标识" width="160" />
                                <el-table-column label="显示名称（可修改）" width="240">
                                    <template slot-scope="scope">
                                        <el-input v-model="scope.row.Label" size="small" placeholder="请输入显示名称" />
                                    </template>
                                </el-table-column>
                                <el-table-column prop="SectionTitle" label="所属分组" min-width="140" show-overflow-tooltip />
                                <el-table-column prop="FieldType" label="控件类型" width="120">
                                    <template slot-scope="scope">
                                        <el-tag size="mini">{{ formatFieldType(scope.row.FieldType) }}</el-tag>
                                    </template>
                                </el-table-column>
                                <el-table-column label="关联字典库" width="160" show-overflow-tooltip>
                                    <template slot-scope="scope">
                                        <span v-if="scope.row.DictKey" class="dict-badge">{{ formatDictKeyName(scope.row.DictKey) }}</span>
                                        <span v-else class="text-muted">-</span>
                                    </template>
                                </el-table-column>
                                <el-table-column label="是否显示" width="100" align="center">
                                    <template slot-scope="scope">
                                        <el-switch v-model="scope.row.IsVisible" :disabled="isLockedField(scope.row.FieldKey)" />
                                    </template>
                                </el-table-column>
                                <el-table-column label="是否必填" width="100" align="center">
                                    <template slot-scope="scope">
                                        <el-switch v-model="scope.row.IsRequired" :disabled="isLockedField(scope.row.FieldKey)" />
                                    </template>
                                </el-table-column>
                                <el-table-column label="排序" width="105" align="center" class-name="sort-order-col">
                                    <template slot-scope="scope">
                                        <el-input-number v-model="scope.row.SortOrder" size="small" :min="0" :max="999" controls-position="right" class="sort-number-input" />
                                    </template>
                                </el-table-column>
                            </el-table>
                        </div>
                    </el-tab-pane>

                    <!-- Tab 3: 班主任（危机预警登记表） -->
                    <el-tab-pane label="班主任（危机预警登记表）" name="archiveHomeroom">
                        <div class="tab-toolbar">
                            <div class="toolbar-tip">
                                <i class="el-icon-info"></i> 配置班主任填写危机筛查表的字段。危机选项是否触发心理评估在「选项库」中设置
                            </div>
                            <div class="toolbar-actions">
                                <el-button size="small" type="primary" icon="el-icon-check" :loading="savingFields" @click="saveFields('archiveHomeroom')">批量保存字段</el-button>
                                <el-button size="small" type="warning" plain icon="el-icon-refresh-left" @click="resetFields('archiveHomeroom')">恢复系统默认</el-button>
                            </div>
                        </div>
                        <div class="tbl-scroll-host">
                            <el-table :data="homeroomFields" v-loading="loadingFields" border class="compact-table" height="100%" style="width: 100%;">
                                <el-table-column prop="FieldKey" label="字段标识" width="160" />
                                <el-table-column label="显示名称（可修改）" width="240">
                                    <template slot-scope="scope">
                                        <el-input v-model="scope.row.Label" size="small" placeholder="请输入显示名称" />
                                    </template>
                                </el-table-column>
                                <el-table-column prop="SectionTitle" label="所属分组" min-width="140" show-overflow-tooltip />
                                <el-table-column prop="FieldType" label="控件类型" width="120">
                                    <template slot-scope="scope">
                                        <el-tag size="mini">{{ formatFieldType(scope.row.FieldType) }}</el-tag>
                                    </template>
                                </el-table-column>
                                <el-table-column label="关联字典库" width="160" show-overflow-tooltip>
                                    <template slot-scope="scope">
                                        <span v-if="scope.row.DictKey" class="dict-badge">{{ formatDictKeyName(scope.row.DictKey) }}</span>
                                        <span v-else class="text-muted">-</span>
                                    </template>
                                </el-table-column>
                                <el-table-column label="是否显示" width="100" align="center">
                                    <template slot-scope="scope">
                                        <el-switch v-model="scope.row.IsVisible" :disabled="isLockedField(scope.row.FieldKey)" />
                                    </template>
                                </el-table-column>
                                <el-table-column label="是否必填" width="100" align="center">
                                    <template slot-scope="scope">
                                        <el-switch v-model="scope.row.IsRequired" :disabled="isLockedField(scope.row.FieldKey)" />
                                    </template>
                                </el-table-column>
                                <el-table-column label="排序" width="105" align="center" class-name="sort-order-col">
                                    <template slot-scope="scope">
                                        <el-input-number v-model="scope.row.SortOrder" size="small" :min="0" :max="999" controls-position="right" class="sort-number-input" />
                                    </template>
                                </el-table-column>
                            </el-table>
                        </div>
                    </el-tab-pane>

                    <!-- Tab 4: 心理老师（高危干预方案） -->
                    <el-tab-pane label="心理老师（高危干预方案）" name="archivePsych">
                        <div class="tab-toolbar">
                            <div class="toolbar-tip">
                                <i class="el-icon-info"></i> 配置心理老师填写高危干预方案的字段。危机等级只在本表，处理指引文案在「心理配置」中维护
                            </div>
                            <div class="toolbar-actions">
                                <el-button size="small" type="primary" icon="el-icon-check" :loading="savingFields" @click="saveFields('archivePsych')">批量保存字段</el-button>
                                <el-button size="small" type="warning" plain icon="el-icon-refresh-left" @click="resetFields('archivePsych')">恢复系统默认</el-button>
                            </div>
                        </div>
                        <div class="tbl-scroll-host">
                            <el-table :data="psychFields" v-loading="loadingFields" border class="compact-table" height="100%" style="width: 100%;">
                                <el-table-column prop="FieldKey" label="字段标识" width="160" />
                                <el-table-column label="显示名称（可修改）" width="240">
                                    <template slot-scope="scope">
                                        <el-input v-model="scope.row.Label" size="small" placeholder="请输入显示名称" />
                                    </template>
                                </el-table-column>
                                <el-table-column prop="SectionTitle" label="所属分组" min-width="140" show-overflow-tooltip />
                                <el-table-column prop="FieldType" label="控件类型" width="120">
                                    <template slot-scope="scope">
                                        <el-tag size="mini">{{ formatFieldType(scope.row.FieldType) }}</el-tag>
                                    </template>
                                </el-table-column>
                                <el-table-column label="关联字典库" width="160" show-overflow-tooltip>
                                    <template slot-scope="scope">
                                        <span v-if="scope.row.DictKey" class="dict-badge">{{ formatDictKeyName(scope.row.DictKey) }}</span>
                                        <span v-else class="text-muted">-</span>
                                    </template>
                                </el-table-column>
                                <el-table-column label="是否显示" width="100" align="center">
                                    <template slot-scope="scope">
                                        <el-switch v-model="scope.row.IsVisible" :disabled="isLockedField(scope.row.FieldKey)" />
                                    </template>
                                </el-table-column>
                                <el-table-column label="是否必填" width="100" align="center">
                                    <template slot-scope="scope">
                                        <el-switch v-model="scope.row.IsRequired" :disabled="isLockedField(scope.row.FieldKey)" />
                                    </template>
                                </el-table-column>
                                <el-table-column label="排序" width="105" align="center" class-name="sort-order-col">
                                    <template slot-scope="scope">
                                        <el-input-number v-model="scope.row.SortOrder" size="small" :min="0" :max="999" controls-position="right" class="sort-number-input" />
                                    </template>
                                </el-table-column>
                            </el-table>
                        </div>
                    </el-tab-pane>

                    <!-- Tab 5: 表单选项字典库 -->
                    <el-tab-pane label="表单选项字典库" name="dict">
                        <div class="dict-nav-bar">
                            <div class="dict-select-item">
                                <span class="label">选择字典分类：</span>
                                <el-select v-model="currentDictKey" placeholder="请选择字典分类" style="width: 260px;" @change="loadDictItems">
                                    <el-option v-for="d in dictKeyOptions" :key="d.key" :label="d.name" :value="d.key" />
                                </el-select>
                            </div>
                            <el-button size="small" type="primary" icon="el-icon-plus" @click="openDictDialog()">
                                新增选项
                            </el-button>
                        </div>

                        <div class="tbl-scroll-host">
                            <el-table :data="dictItems" v-loading="loadingDict" border class="compact-table" height="100%" style="width: 100%;">
                                <el-table-column prop="OptionCode" label="选项编码" width="130" />
                                <el-table-column prop="OptionName" label="选项文本内容" min-width="160" />
                                <el-table-column label="触发心理评估" width="140" align="center">
                                    <template slot-scope="scope">
                                        <el-tag v-if="scope.row.TriggersPsychReview" size="small" type="danger" effect="plain">触发评估</el-tag>
                                        <span v-else class="text-muted">不触发</span>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="SortOrder" label="排序号" width="90" align="center" />
                                <el-table-column label="状态" width="100" align="center">
                                    <template slot-scope="scope">
                                        <el-tag :type="scope.row.Enabled ? 'success' : 'info'" size="small">
                                            {{ scope.row.Enabled ? '启用' : '禁用' }}
                                        </el-tag>
                                    </template>
                                </el-table-column>
                                <el-table-column label="操作" width="150" align="center">
                                    <template slot-scope="scope">
                                        <el-button size="mini" type="text" icon="el-icon-edit" @click="openDictDialog(scope.row)">编辑</el-button>
                                        <el-button size="mini" type="text" style="color: #F56C6C;" icon="el-icon-delete" @click="handleDeleteDict(scope.row)">删除</el-button>
                                    </template>
                                </el-table-column>
                            </el-table>
                        </div>
                    </el-tab-pane>
                </el-tabs>
            </template>

            <!-- 未选择学校提示 -->
            <div v-else class="no-department-tip">
                <div class="no-department-tip__inner">
                    <i class="el-icon-school"></i>
                    <p>请在左侧选择所属学校，查看与配置表单字段与选项库</p>
                </div>
            </div>
        </section>

            <!-- 字典选项新增/编辑弹窗 -->
            <el-dialog :title="dictDialogTitle" :visible.sync="dictDialogVisible" width="500px" :close-on-click-modal="false">
                <el-form :model="dictForm" label-width="120px" :rules="dictRules" ref="dictForm">
                    <el-form-item label="选项名称" prop="OptionName">
                        <el-input v-model="dictForm.OptionName" placeholder="例如：有严重人际社交障碍" />
                    </el-form-item>
                    <el-form-item v-if="isCrisisDict" label="触发心理评估">
                        <el-switch v-model="dictForm.TriggersPsychReview" active-text="命中后待评估" inactive-text="不触发" />
                        <span class="sub-hint">班主任表勾选此项后，该生自动进入心理老师待评估名单</span>
                    </el-form-item>
                    <el-form-item label="排序号">
                        <el-input-number v-model="dictForm.SortOrder" :min="0" :max="999" controls-position="right" />
                    </el-form-item>
                    <el-form-item label="启用状态">
                        <el-switch v-model="dictForm.Enabled" />
                    </el-form-item>
                </el-form>
                <div slot="footer" class="dialog-footer">
                    <el-button @click="dictDialogVisible = false">取 消</el-button>
                    <el-button type="primary" :loading="savingDict" @click="submitDictForm">确 定</el-button>
                </div>
            </el-dialog>

            <!-- 复制其他校区配置弹窗 -->
            <el-dialog
                title="复制其他校区心理配置"
                :visible.sync="copyDialogVisible"
                width="620px"
                append-to-body
                :destroy-on-close="true"
                :close-on-click-modal="false"
                custom-class="psychology-copy-dialog"
                top="10vh"
            >
                <div class="copy-dialog-content">
                    <el-alert
                        title="说明：支持将其他校区成熟的配置一键复制并覆盖到当前校区。历史已填写的学生档案不受影响。"
                        type="info"
                        show-icon
                        :closable="false"
                        style="margin-bottom: 18px;"
                    />

                    <el-form label-width="110px" size="small" class="copy-form">
                        <el-form-item label="当前目标校区">
                            <el-tag size="medium" type="success" effect="plain" style="font-weight: bold;">
                                {{ currentCampusName || '未选定' }}
                            </el-tag>
                            <span class="text-muted" style="margin-left: 8px; font-size: 12px;">(配置将覆盖此校区)</span>
                        </el-form-item>

                        <el-form-item label="选择来源校区" required>
                            <el-select
                                v-model="copySourceCampusId"
                                placeholder="请选择作为模板的来源校区"
                                style="width: 100%;"
                                :disabled="sourceCampusOptions.length === 0"
                            >
                                <el-option
                                    v-for="item in sourceCampusOptions"
                                    :key="item.Id"
                                    :label="item.Name"
                                    :value="item.Id"
                                />
                            </el-select>
                            <div v-if="sourceCampusOptions.length === 0" style="color: #e6a23c; font-size: 12px; margin-top: 4px; line-height: 1.4;">
                                <i class="el-icon-warning"></i> 当前学校暂无其他可选校区作为模板
                            </div>
                        </el-form-item>

                        <el-form-item label="复制内容范围" required>
                            <div class="copy-scope-box">
                                <div class="scope-header">
                                    <el-checkbox
                                        v-model="copyCheckAll"
                                        :indeterminate="isCopyIndeterminate"
                                        @change="handleCopyCheckAllChange"
                                    >
                                        全选所有配置
                                    </el-checkbox>
                                </div>
                                <div class="scope-list">
                                    <el-checkbox v-model="copyScope.copyCampusConfig" @change="handleCopyScopeChange">
                                        <span class="scope-main">1. 业务功能开关与时段模式</span>
                                        <span class="scope-hint">预约/档案开启状态、签名与处理指引</span>
                                    </el-checkbox>
                                    <el-checkbox v-model="copyScope.copyAppointmentFields" @change="handleCopyScopeChange">
                                        <span class="scope-main">2. 心理预约表单字段</span>
                                        <span class="scope-hint">填报字段、必填、显隐与排序</span>
                                    </el-checkbox>
                                    <el-checkbox v-model="copyScope.copyArchiveParentFields" @change="handleCopyScopeChange">
                                        <span class="scope-main">3. 家长表字段</span>
                                        <span class="scope-hint">学生心理档案</span>
                                    </el-checkbox>
                                    <el-checkbox v-model="copyScope.copyArchiveHomeroomFields" @change="handleCopyScopeChange">
                                        <span class="scope-main">4. 班主任表字段</span>
                                        <span class="scope-hint">危机预警登记表</span>
                                    </el-checkbox>
                                    <el-checkbox v-model="copyScope.copyArchivePsychFields" @change="handleCopyScopeChange">
                                        <span class="scope-main">5. 心理老师表字段</span>
                                        <span class="scope-hint">高危干预方案</span>
                                    </el-checkbox>
                                    <el-checkbox v-model="copyScope.copyDictItems" @change="handleCopyScopeChange">
                                        <span class="scope-main">6. 表单选项字典库</span>
                                        <span class="scope-hint">危机筛查、病史、学习情况等选项</span>
                                    </el-checkbox>
                                    <el-checkbox v-model="copyScope.copyGuideTexts" @change="handleCopyScopeChange">
                                        <span class="scope-main">7. 三类处理指引文案</span>
                                        <span class="scope-hint">心理老师表右侧说明</span>
                                    </el-checkbox>
                                </div>
                            </div>
                        </el-form-item>
                    </el-form>

                    <div class="copy-warning-footer">
                        <i class="el-icon-warning" style="color: #E6A23C; margin-right: 4px;"></i>
                        <span>注意：复制执行后将使用来源校区数据替换目标校区已勾选的配置，请谨慎操作。</span>
                    </div>
                </div>

                <div slot="footer" class="dialog-footer">
                    <el-button size="small" @click="copyDialogVisible = false">取 消</el-button>
                    <el-button
                        size="small"
                        type="primary"
                        :loading="copying"
                        :disabled="!copySourceCampusId || sourceCampusOptions.length === 0"
                        @click="confirmCopyConfig"
                    >
                        确认复制并覆盖
                    </el-button>
                </div>
            </el-dialog>
    </section>
</template>

<script>
import DepartmentPane from '../../components/DepartmentPane';
import { getCampusList } from '../../api/modules/psychology';
import {
    getPsychologyFormFields,
    batchUpdatePsychologyFormFields,
    resetDefaultPsychologyFormFields,
    getPsychologyDictItems,
    savePsychologyDictItem,
    deletePsychologyDictItem,
    copyCampusPsychologyConfig
} from '../../api/modules/psychology';
import {
    resolveActiveSchoolId,
    normalizeDeptId,
    treeNodeDeptId,
} from '../../utils/deptCascaderProps';

export default {
    name: 'PsychologyFormConfig',
    components: { DepartmentPane },
    data() {
        return {
            schoolId: '',
            schoolName: '',
            campusId: null,
            campusOptions: [],
            activeTab: 'appointment',

            // 复制配置
            copyDialogVisible: false,
            copySourceCampusId: null,
            copying: false,
            copyCheckAll: true,
            isCopyIndeterminate: false,
            copyScope: {
                copyCampusConfig: true,
                copyAppointmentFields: true,
                copyArchiveParentFields: true,
                copyArchiveHomeroomFields: true,
                copyArchivePsychFields: true,
                copyDictItems: true,
                copyGuideTexts: true
            },

            appointmentFields: [],
            parentFields: [],
            homeroomFields: [],
            psychFields: [],
            loadingFields: false,
            savingFields: false,

            currentDictKey: 'severeCrisis',
            dictKeyOptions: [
                { key: 'onlyChild', name: '是否独生 (onlyChild)' },
                { key: 'studyLevel', name: '学习情况 (studyLevel)' },
                { key: 'healthStatus', name: '健康状况 (healthStatus)' },
                { key: 'medicalHistory', name: '过往病史 (medicalHistory)' },
                { key: 'specialSituation', name: '特殊情况 (specialSituation)' },
                { key: 'parentRelation', name: '父母关系 (parentRelation)' },
                { key: 'familyAtmosphere', name: '家庭气氛 (familyAtmosphere)' },
                { key: 'favorLevel', name: '喜爱程度 (favorLevel)' },
                { key: 'collectiveFavor', name: '对集体喜爱程度 (collectiveFavor)' },
                { key: 'severeCrisis', name: '严重心理危机筛查 (severeCrisis)' },
                { key: 'majorCrisis', name: '重大心理危机评估 (majorCrisis)' },
                { key: 'attentionLevel', name: '危机等级 (attentionLevel)' }
            ],
            dictItems: [],
            loadingDict: false,
            dictDialogVisible: false,
            dictDialogTitle: '',
            savingDict: false,
            dictForm: {
                Id: 0,
                SchoolId: 0,
                CampusId: 0,
                DictKey: '',
                OptionCode: '',
                OptionName: '',
                TriggersPsychReview: false,
                SortOrder: 0,
                Enabled: true
            },
            dictRules: {
                OptionName: [{ required: true, message: '请输入选项名称', trigger: 'blur' }]
            }
        };
    },
    computed: {
        hasSchoolSelected() {
            return !!normalizeDeptId(this.schoolId);
        },
        isCrisisDict() {
            return this.currentDictKey === 'severeCrisis' || this.currentDictKey === 'majorCrisis';
        },
        currentCampusName() {
            const curId = String(this.campusId || '');
            const found = (this.campusOptions || []).find(c => {
                const cid = String(c.Id != null ? c.Id : (c.id != null ? c.id : (c.value != null ? c.value : '')));
                return cid === curId;
            });
            return found ? (found.Name || found.name || found.label || '当前校区') : '';
        },
        sourceCampusOptions() {
            const curId = String(this.campusId || '');
            return (this.campusOptions || []).filter(c => {
                const cid = String(c.Id != null ? c.Id : (c.id != null ? c.id : (c.value != null ? c.value : '')));
                return cid && cid !== curId;
            }).map(c => ({
                Id: String(c.Id != null ? c.Id : (c.id != null ? c.id : c.value)),
                Name: c.Name || c.name || c.label || '未命名校区'
            }));
        }
    },
    methods: {
        // ================= 复制其他校区配置 =================

        openCopyDialog() {
            if (!this.campusId) {
                this.$message.warning('请先选择当前目标校区');
                return;
            }
            if (this.sourceCampusOptions.length === 0) {
                this.$message.warning('当前学校仅有 1 个校区，暂无可供复制的模板校区');
                return;
            }
            this.copySourceCampusId = null;
            this.copyCheckAll = true;
            this.isCopyIndeterminate = false;
            this.copyScope = {
                copyCampusConfig: true,
                copyAppointmentFields: true,
                copyArchiveParentFields: true,
                copyArchiveHomeroomFields: true,
                copyArchivePsychFields: true,
                copyDictItems: true,
                copyGuideTexts: true
            };
            this.copyDialogVisible = true;
        },

        handleCopyCheckAllChange(val) {
            this.copyScope = {
                copyCampusConfig: val,
                copyAppointmentFields: val,
                copyArchiveParentFields: val,
                copyArchiveHomeroomFields: val,
                copyArchivePsychFields: val,
                copyDictItems: val,
                copyGuideTexts: val
            };
            this.isCopyIndeterminate = false;
        },

        handleCopyScopeChange() {
            const values = Object.values(this.copyScope);
            const trueCount = values.filter(Boolean).length;
            this.copyCheckAll = trueCount === values.length;
            this.isCopyIndeterminate = trueCount > 0 && trueCount < values.length;
        },

        async confirmCopyConfig() {
            if (!this.copySourceCampusId) {
                this.$message.warning('请选择来源校区');
                return;
            }
            const hasChecked = Object.values(this.copyScope).some(Boolean);
            if (!hasChecked) {
                this.$message.warning('请至少勾选一项需要复制的配置内容');
                return;
            }

            const sourceCampus = this.campusOptions.find(c => c.Id === this.copySourceCampusId);
            const sourceName = sourceCampus ? sourceCampus.Name : '所选校区';
            const targetName = this.currentCampusName;

            try {
                await this.$confirm(
                    `确定要将【${sourceName}】的配置复制并覆盖到当前【${targetName}】吗？此操作无法撤销。`,
                    '复制确认',
                    {
                        confirmButtonText: '确定覆盖',
                        cancelButtonText: '取消',
                        type: 'warning'
                    }
                );
            } catch (e) {
                return;
            }

            this.copying = true;
            try {
                const res = await copyCampusPsychologyConfig({
                    schoolId: this.schoolId,
                    sourceCampusId: this.copySourceCampusId,
                    targetCampusId: this.campusId,
                    copyCampusConfig: this.copyScope.copyCampusConfig,
                    copyAppointmentFields: this.copyScope.copyAppointmentFields,
                    copyArchiveParentFields: this.copyScope.copyArchiveParentFields,
                    copyArchiveHomeroomFields: this.copyScope.copyArchiveHomeroomFields,
                    copyArchivePsychFields: this.copyScope.copyArchivePsychFields,
                    copyDictItems: this.copyScope.copyDictItems,
                    copyGuideTexts: this.copyScope.copyGuideTexts
                });

                if (res && res.data && res.data.success) {
                    this.$message.success('配置复制成功！已自动加载最新配置');
                    this.copyDialogVisible = false;
                    this.refreshCurrentTab();
                } else {
                    this.$message.error((res && res.data && res.data.msg) || '复制失败');
                }
            } catch (error) {
                this.$message.error('复制出错: ' + (error.message || error));
            } finally {
                this.copying = false;
            }
        },

        resolveSchoolId() {
            const fromStore = resolveActiveSchoolId(this);
            if (fromStore) return fromStore;
            return normalizeDeptId(this.schoolId);
        },

        treeNodeClick(data) {
            this.schoolId = treeNodeDeptId(data);
            this.schoolName = data.label || '';
            this.campusId = null;
            this.campusOptions = [];
            this.loadCampusOptions();
        },

        async loadCampusOptions() {
            if (!this.schoolId) return;
            try {
                const res = await getCampusList({ page: 1, intPageSize: 999, departmentId: this.schoolId });
                if (res.data && res.data.success) {
                    this.campusOptions = res.data.response.data || [];
                    if (this.campusOptions.length > 0) {
                        this.campusId = this.campusOptions[0].Id;
                        this.refreshCurrentTab();
                    }
                }
            } catch (err) {
                this.$message.error('加载校区失败');
            }
        },

        handleCampusChange() {
            if (this.campusId) {
                this.refreshCurrentTab();
            }
        },

        handleTabClick() {
            this.refreshCurrentTab();
        },

        refreshCurrentTab() {
            if (!this.schoolId || !this.campusId) return;
            if (this.activeTab === 'dict') {
                this.loadDictItems();
            } else {
                this.loadFields(this.activeTab);
            }
        },

        isLockedField(fieldKey) {
            return ['studentId', 'appointDate', 'studentName', 'attentionLevel', 'severeCrisis', 'majorCrisis'].indexOf(fieldKey) >= 0;
        },

        formatFieldType(type) {
            const map = {
                studentPicker: '学生选择器',
                text: '单行文本',
                textarea: '多行文本',
                date: '日期选择',
                select: '下拉选择',
                multiSelect: '多选勾选',
                radio: '单选按钮',
                image: '图片上传',
                address: '地址输入',
                staticText: '静态提示文案',
                repeater: '多行表格'
            };
            return map[type] || type;
        },

        /** 字典库标识转中文名。创建于 2026-09-19 */
        formatDictKeyName(key) {
            if (!key) return '';
            const found = (this.dictKeyOptions || []).find(d => d.key === key);
            if (!found || !found.name) return key;
            const name = String(found.name);
            const idx = name.indexOf(' (');
            return idx > 0 ? name.slice(0, idx) : name;
        },

        // ================= 字段管理 =================

        async loadFields(bizType) {
            this.loadingFields = true;
            try {
                const res = await getPsychologyFormFields({
                    schoolId: this.schoolId,
                    campusId: this.campusId,
                    bizType
                });
                if (res.data && res.data.success) {
                    const list = res.data.response || [];
                    if (bizType === 'appointment') this.appointmentFields = list;
                    else if (bizType === 'archiveParent') this.parentFields = list;
                    else if (bizType === 'archiveHomeroom') this.homeroomFields = list;
                    else if (bizType === 'archivePsych') this.psychFields = list;
                }
            } catch (err) {
                this.$message.error('加载字段配置失败');
            } finally {
                this.loadingFields = false;
            }
        },

        async saveFields(bizType) {
            const map = {
                appointment: this.appointmentFields,
                archiveParent: this.parentFields,
                archiveHomeroom: this.homeroomFields,
                archivePsych: this.psychFields
            };
            const list = map[bizType] || [];
            if (!list || list.length === 0) return;

            this.savingFields = true;
            try {
                const res = await batchUpdatePsychologyFormFields(list);
                if (res.data && res.data.success) {
                    this.$message.success('表单字段配置已保存');
                    this.loadFields(bizType);
                } else {
                    this.$message.error(res.data ? res.data.msg : '保存失败');
                }
            } catch (err) {
                this.$message.error('保存失败，请稍后重试');
            } finally {
                this.savingFields = false;
            }
        },

        resetFields(bizType) {
            this.$confirm('确定要将该业务表单字段重置为系统出厂默认配置吗？自定义的字段显示名称与排序将被重置。', '重置提示', {
                type: 'warning'
            }).then(async () => {
                try {
                    const res = await resetDefaultPsychologyFormFields({
                        schoolId: this.schoolId,
                        campusId: this.campusId,
                        bizType
                    });
                    if (res.data && res.data.success) {
                        this.$message.success('已恢复默认配置');
                        this.loadFields(bizType);
                    } else {
                        this.$message.error(res.data ? res.data.msg : '重置失败');
                    }
                } catch (err) {
                    this.$message.error('重置请求异常');
                }
            }).catch(() => {});
        },

        // ================= 选项字典管理 =================

        async loadDictItems() {
            if (!this.schoolId || !this.campusId || !this.currentDictKey) return;
            this.loadingDict = true;
            try {
                const res = await getPsychologyDictItems({
                    schoolId: this.schoolId,
                    campusId: this.campusId,
                    dictKey: this.currentDictKey
                });
                if (res.data && res.data.success) {
                    this.dictItems = res.data.response || [];
                }
            } catch (err) {
                this.$message.error('加载字典选项失败');
            } finally {
                this.loadingDict = false;
            }
        },

        openDictDialog(row) {
            if (row) {
                this.dictDialogTitle = '编辑字典选项';
                this.dictForm = {
                    Id: row.Id,
                    SchoolId: this.schoolId,
                    CampusId: this.campusId,
                    DictKey: row.DictKey,
                    OptionCode: row.OptionCode,
                    OptionName: row.OptionName,
                    TriggersPsychReview: !!row.TriggersPsychReview,
                    SortOrder: row.SortOrder || 0,
                    Enabled: !!row.Enabled
                };
            } else {
                this.dictDialogTitle = '新增字典选项';
                this.dictForm = {
                    Id: 0,
                    SchoolId: this.schoolId,
                    CampusId: this.campusId,
                    DictKey: this.currentDictKey,
                    OptionCode: '',
                    OptionName: '',
                    TriggersPsychReview: this.isCrisisDict,
                    SortOrder: (this.dictItems.length + 1) * 10,
                    Enabled: true
                };
            }
            this.dictDialogVisible = true;
            this.$nextTick(() => this.$refs.dictForm && this.$refs.dictForm.clearValidate());
        },

        submitDictForm() {
            this.$refs.dictForm.validate(async (valid) => {
                if (!valid) return;
                this.savingDict = true;
                try {
                    const res = await savePsychologyDictItem(this.dictForm);
                    if (res.data && res.data.success) {
                        this.$message.success('选项保存成功');
                        this.dictDialogVisible = false;
                        this.loadDictItems();
                    } else {
                        this.$message.error(res.data ? res.data.msg : '保存失败');
                    }
                } catch (err) {
                    this.$message.error('保存失败');
                } finally {
                    this.savingDict = false;
                }
            });
        },

        handleDeleteDict(row) {
            this.$confirm(`确认删除选项【${row.OptionName}】吗？`, '删除提示', { type: 'warning' })
                .then(async () => {
                    const res = await deletePsychologyDictItem({ id: row.Id });
                    if (res.data && res.data.success) {
                        this.$message.success('删除成功');
                        this.loadDictItems();
                    } else {
                        this.$message.error(res.data ? res.data.msg : '删除失败');
                    }
                }).catch(() => {});
        }
    },
    mounted() {
        const defaultSchoolId = this.resolveSchoolId();
        if (defaultSchoolId) {
            this.schoolId = defaultSchoolId;
            this.loadCampusOptions();
        }
    }
};
</script>

<style scoped>
.header-bar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 14px;
    background: #f8fafc;
    border: 1px solid #eef2f6;
    border-radius: 6px;
    margin-bottom: 8px;
}

.school-info .label,
.campus-selector .label {
    font-size: 14px;
    color: #606266;
    line-height: 32px;
    white-space: nowrap;
}

.school-info .value {
    font-size: 15px;
    font-weight: 600;
    color: #303133;
}

.campus-selector {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
}

.campus-select {
    width: 200px;
}

.copy-campus-btn {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    height: 32px;
    line-height: 1 !important;
    padding: 0 12px !important;
    vertical-align: middle;
}

.copy-campus-btn >>> [class^="el-icon-"] {
    margin-right: 4px;
    line-height: 1;
    font-size: 14px;
}

.tab-toolbar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 0 8px 0;
}

.toolbar-tip {
    font-size: 13px;
    color: #64748b;
    display: flex;
    align-items: center;
}

.toolbar-tip i {
    color: #409EFF;
    margin-right: 4px;
}

.dict-nav-bar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 0 8px 0;
}

.dict-select-item .label {
    font-size: 14px;
    color: #606266;
    margin-right: 8px;
}

.dict-badge {
    background: #ecf5ff;
    color: #409eff;
    border: 1px solid #d9ecff;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
}

.text-muted {
    color: #c0c4cc;
}

.custom-tag-preview {
    color: #fff;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    display: inline-block;
}

.color-badge {
    display: inline-block;
    width: 14px;
    height: 14px;
    border-radius: 3px;
    margin-right: 6px;
    vertical-align: middle;
}

.sub-hint {
    font-size: 12px;
    color: #909399;
    display: block;
    line-height: 1.4;
    margin-top: 4px;
}

.sort-order-col >>> .cell {
    padding-left: 0 !important;
    padding-right: 0 !important;
    overflow: visible !important;
    text-overflow: clip !important;
}

.sort-number-input {
    width: 78px !important;
}

.sort-number-input >>> .el-input__inner {
    padding-left: 6px !important;
    padding-right: 25px !important;
    text-align: center !important;
    font-size: 13px !important;
}

.sort-number-input >>> .el-input-number__increase,
.sort-number-input >>> .el-input-number__decrease {
    width: 24px !important;
}

.no-campus-tip,
.no-department-tip {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 200px;
    color: #909399;
}

.no-campus-tip i,
.no-department-tip i {
    font-size: 48px;
    margin-bottom: 12px;
    color: #c0c4cc;
}

.copy-dialog-content {
    padding: 0 4px;
    overflow-x: hidden;
}

.copy-scope-box {
    border: 1px solid #DCDFE6;
    border-radius: 4px;
    padding: 10px 14px;
    background-color: #FAFAFA;
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;
}

.scope-header {
    border-bottom: 1px solid #EBEEF5;
    padding-bottom: 8px;
    margin-bottom: 8px;
}

.scope-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.scope-main {
    display: block;
    color: #303133;
    line-height: 1.4;
}

.scope-hint {
    display: block;
    color: #909399;
    font-size: 12px;
    line-height: 1.4;
    margin-top: 2px;
    font-weight: normal;
}

.copy-warning-footer {
    margin-top: 14px;
    padding: 10px 12px;
    background: #FDF6EC;
    border: 1px solid #FAECD8;
    border-radius: 4px;
    color: #E6A23C;
    font-size: 12px;
    line-height: 1.5;
}
</style>

<style>
/* 针对 append-to-body 弹窗的全局样式渗透 */
.psychology-copy-dialog .el-dialog__body {
    overflow-x: hidden;
    padding-top: 14px;
    padding-bottom: 10px;
}
.psychology-copy-dialog .copy-dialog-content {
    padding: 0 4px;
    overflow-x: hidden;
}
.psychology-copy-dialog .copy-form .el-form-item__content {
    line-height: normal;
    overflow: hidden;
}
.psychology-copy-dialog .copy-scope-box {
    border: 1px solid #DCDFE6;
    border-radius: 4px;
    padding: 10px 14px;
    background-color: #FAFAFA;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow: hidden;
}
.psychology-copy-dialog .scope-header {
    border-bottom: 1px solid #EBEEF5;
    padding-bottom: 8px;
    margin-bottom: 8px;
}
.psychology-copy-dialog .scope-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.psychology-copy-dialog .scope-list .el-checkbox {
    display: flex;
    align-items: flex-start;
    margin-right: 0;
    white-space: normal;
    height: auto;
    width: 100%;
}
.psychology-copy-dialog .scope-list .el-checkbox__input {
    margin-top: 3px;
    flex-shrink: 0;
}
.psychology-copy-dialog .scope-list .el-checkbox__label {
    white-space: normal;
    line-height: 1.4;
    flex: 1;
    min-width: 0;
    word-break: break-word;
    padding-left: 8px;
}
.psychology-copy-dialog .scope-main {
    display: block;
    color: #303133;
    line-height: 1.4;
}
.psychology-copy-dialog .scope-hint {
    display: block;
    color: #909399;
    font-size: 12px;
    line-height: 1.4;
    margin-top: 2px;
    font-weight: normal;
}
.psychology-copy-dialog .copy-warning-footer {
    margin-top: 14px;
    padding: 10px 12px;
    background: #FDF6EC;
    border: 1px solid #FAECD8;
    border-radius: 4px;
    color: #E6A23C;
    font-size: 12px;
    line-height: 1.5;
}
</style>
